/**
 * Non-reactive instance state for the inspector details pane: reactive
 * controllers, `@lit/task` instances, signals and plain class fields. Runs in
 * the inspected page and reads a live element, so everything is duck-typed (no
 * dependency on the libraries) and read-only: no `run()`, no `set()`, no
 * getter on an unrecognised object, and a signal that could run user code is
 * never evaluated.
 */

import {isExpandable} from './inspect-value.js';
import {serialize, typeTag} from './serialize.js';
import {idOf} from '../timeline/identity.js';
import {isListed} from './listed.js';
import {
  clip,
  controllersOf,
  ctorName,
  dataProp,
  hasFn,
  isObject,
  type Dict,
} from '../live-reads.js';
import {TASK_ERROR, taskStatus, taskStatusName} from '../lit-tasks.js';
import type {
  AnatomyElementRef,
  InspectorContext,
  InspectorExtra,
} from '../../../types/inspector.js';

/** Most extras reported for one element; keeps a field-heavy class cheap. */
const MAX_EXTRAS = 24;
/** Consumers listed on a provider; the rest are counted. */
const MAX_CONSUMERS = 12;
/** Ancestors walked looking for a consumer's provider. */
const MAX_DEPTH = 256;

/**
 * Own fields that belong to Lit or the DOM rather than to the component. The
 * `__` and `_$` prefixes (dev and mangled production internals) are skipped by
 * prefix instead.
 */
const LIT_FIELDS = new Set([
  'renderRoot',
  'renderOptions',
  'isUpdatePending',
  'hasUpdated',
  'isConnected',
  'updateComplete',
]);

/**
 * Preview of `read()`'s result and whether it expands, from one read: a
 * signal read inside a computed subscribes it once per read. A throwing
 * getter degrades to a placeholder instead of dropping the entry.
 */
const sample = (read: () => unknown): {value: string; expandable?: true} => {
  try {
    const v = read();
    return isExpandable(v)
      ? {value: serialize(v), expandable: true}
      : {value: serialize(v)};
  } catch {
    return {value: '[getter threw]'};
  }
};

const refOf = (el: Element): AnatomyElementRef => ({
  tagName: el.localName,
  ...(isListed(el) ? {id: idOf(el)} : {}),
});

/**
 * `@lit/context` `ContextProvider`: a `ValueNotifier` (a `subscriptions` Map
 * and `addCallback`) with a `context` key and a `host`. Same class whether
 * made directly or by `@provide`.
 */
const isProvider = (v: Dict): boolean =>
  dataProp(v, 'subscriptions')?.value instanceof Map &&
  hasFn(v, 'addCallback') &&
  hasFn(v, 'onContextRequest') &&
  dataProp(v, 'context') !== undefined;

/**
 * `ContextConsumer`: a `context` key, a `host`, boolean `subscribe` and
 * `provided` flags and `dispatchRequest`. The callback it hands to providers
 * is `_callback` in the development build but renamed in production, so it is
 * not part of the shape.
 */
const isConsumer = (v: Dict): boolean =>
  dataProp(v, 'context') !== undefined &&
  dataProp(v, 'host')?.value instanceof Element &&
  typeof dataProp(v, 'subscribe')?.value === 'boolean' &&
  typeof dataProp(v, 'provided')?.value === 'boolean' &&
  hasFn(v, 'dispatchRequest');

const contextKey = (key: unknown): string =>
  clip(
    typeof key === 'symbol' || typeof key === 'string'
      ? String(key)
      : serialize(key)
  );

/** Providers of `context` on `el` itself. */
const providersOn = (el: Element, context: unknown): Dict[] =>
  controllersOf(el).filter(
    (c) => isProvider(c as Dict) && dataProp(c, 'context')?.value === context
  ) as Dict[];

type Subscriptions = Map<unknown, {consumerHost?: unknown}>;

/**
 * The node one step up from `el` on a composed event's path: the slot it is
 * assigned to when it is slotted, else its parent, crossing from a shadow
 * root to its host. Through a slot, the walk visits the shadow tree around
 * the slot before the host, as a `context-request` does.
 */
const parentElementOf = (el: Node): Node | null => {
  const slot = el instanceof Element ? el.assignedSlot : null;
  const parent: Node | null =
    slot ?? el.parentNode ?? (el instanceof ShadowRoot ? el.host : null);
  return parent instanceof ShadowRoot ? parent.host : parent;
};

/** Whether `host` sits in the subscriptions of the provider `p`. */
const hasSubscriber = (p: Dict, host: Element): boolean => {
  const subs = dataProp(p, 'subscriptions')?.value as Subscriptions;
  for (const {consumerHost} of subs.values()) {
    if (consumerHost === host) return true;
  }
  return false;
};

/**
 * The provider that answers a consumer, by what `ContextProvider` does with
 * the request: it bubbles (composed) from the consumer's host and the first
 * matching provider above it takes it. A subscribing consumer is confirmed
 * by its host sitting in that provider's subscriptions; a non-subscribing
 * one leaves no trace, so the nearest provider of the key stands in.
 */
const providerOf = (consumer: Dict): Element | undefined => {
  const host = dataProp(consumer, 'host')?.value;
  if (!(host instanceof Element)) return undefined;
  const context = dataProp(consumer, 'context')?.value;
  let nearest: Element | undefined;
  let el: Node | null = host;
  for (let i = 0; el !== null && i < MAX_DEPTH; i++) {
    el = parentElementOf(el);
    if (!(el instanceof Element)) continue;
    for (const p of providersOn(el, context)) {
      if (hasSubscriber(p, host)) return el;
      nearest ??= el;
    }
  }
  const subscribed = dataProp(consumer, 'subscribe')?.value === true;
  return subscribed && dataProp(consumer, 'unsubscribe')?.value !== undefined
    ? undefined
    : nearest;
};

/**
 * Calls `onChange` when a context provider on `el` hands its subscribers a
 * new value. A provider's `setValue` does not update its own host, so the
 * host's details would otherwise show the old value until something else
 * renders it. Wraps each provider's own `updateObservers` (an instance arrow
 * function, kept by name in the production build) on this element only;
 * returns a function that puts them back.
 */
export const onProviderChange = (
  el: Element,
  onChange: () => void
): (() => void) => {
  const restores = controllersOf(el)
    .filter((c) => isProvider(c as Dict))
    .flatMap((c) => {
      const p = c as {updateObservers?: unknown};
      const prev = p.updateObservers;
      if (typeof prev !== 'function') return [];
      const wrapped = function (this: unknown, ...args: unknown[]) {
        (prev as (...a: unknown[]) => unknown).apply(this, args);
        onChange();
      };
      p.updateObservers = wrapped;
      return [
        () => {
          if (p.updateObservers === wrapped) p.updateObservers = prev;
        },
      ];
    });
  return () => {
    for (const restore of restores) restore();
  };
};

/** Fill in the consumers a provider serves, capped at {@link MAX_CONSUMERS}. */
const describeConsumers = (info: InspectorContext, v: Dict): void => {
  const subs = dataProp(v, 'subscriptions')?.value as Subscriptions;
  const hosts = new Set<Element>();
  for (const {consumerHost} of subs.values()) {
    if (consumerHost instanceof Element) hosts.add(consumerHost);
  }
  const all = [...hosts];
  if (all.length > 0) info.consumers = all.slice(0, MAX_CONSUMERS).map(refOf);
  if (all.length > MAX_CONSUMERS) {
    info.moreConsumers = all.length - MAX_CONSUMERS;
  }
};

/** Context details for a provider or consumer controller; `undefined` for neither. */
const classifyContext = (
  v: Dict,
  name: string
): {extra: InspectorExtra; raw: () => unknown} | undefined => {
  const provider = isProvider(v);
  if (!provider && !isConsumer(v)) return undefined;
  const key = contextKey(dataProp(v, 'context')?.value);
  const info: InspectorContext = {
    role: provider ? 'provider' : 'consumer',
    key,
  };
  if (provider) {
    describeConsumers(info, v);
  } else if (dataProp(v, 'provided')?.value === false) {
    // Nothing answered the request, so a provider nearby is not its source:
    // it connected later, or does not match. A subscribing request can still
    // be answered when a `ContextRoot` replays it.
    if (dataProp(v, 'subscribe')?.value === true) info.waiting = true;
  } else {
    const found = providerOf(v);
    if (found !== undefined) info.provider = refOf(found);
  }
  // ValueNotifier's `value` is a plain getter over `_value`; a consumer
  // stores `value` as a field.
  const raw = provider ? () => v['value'] : () => dataProp(v, 'value')?.value;
  return {
    extra: {
      kind: 'context',
      name,
      ...sample(raw),
      type: provider ? 'ContextProvider' : 'ContextConsumer',
      context: info,
    },
    raw,
  };
};

/** An extra and a way to read the live value its preview shows. */
interface ExtraEntry {
  extra: InspectorExtra;
  /** The value behind the row, for expanding it; may throw. */
  raw: (() => unknown) | undefined;
}

/** Classify one object the element holds; `undefined` if it is none of ours. */
const classify = (
  v: Dict,
  name: string,
  controller: boolean
): ExtraEntry | undefined => {
  // @lit/task: a controller with a numeric status, run() and render().
  const status = taskStatus(v);
  if (status !== undefined) {
    const raw = () => (status === TASK_ERROR ? v['error'] : v['value']);
    return {
      extra: {
        kind: 'task',
        name,
        ...sample(raw),
        type: 'Task',
        status: taskStatusName(status),
      },
      raw,
    };
  }
  // Signals (signal-polyfill): State reads are side-effect free, but Computed
  // runs user code and lazily recomputes, so it is reported without reading.
  const ctor = ctorName(v);
  if (ctor === 'Computed' && hasFn(v, 'get')) {
    return {
      extra: {kind: 'signal', name, value: '(computed)', type: 'Computed'},
      raw: undefined,
    };
  }
  if (ctor === 'State' && hasFn(v, 'get') && hasFn(v, 'set')) {
    const raw = () => (v['get'] as () => unknown)();
    return {
      extra: {kind: 'signal', name, ...sample(raw), type: 'Signal.State'},
      raw,
    };
  }
  if (!controller) return undefined;
  const ctx = classifyContext(v, name);
  if (ctx !== undefined) return ctx;
  const held = dataProp(v, 'value');
  return {
    extra: {
      kind: 'controller',
      name,
      value: held === undefined ? ctor : serialize(held.value),
      type: ctor,
      ...(isExpandable(held === undefined ? v : held.value)
        ? {expandable: true}
        : {}),
    },
    // A controller without a value expands to its own fields.
    raw: held === undefined ? () => v : () => held.value,
  };
};

/**
 * The element's non-reactive instance state, capped at {@link MAX_EXTRAS}:
 * controllers and tasks first (in registration order), then signals and tasks
 * held in own fields, then plain own fields. Never throws.
 */
export const collectExtras = (el: Element): InspectorExtra[] =>
  collectEntries(el).map((e) => e.extra);

/**
 * The live value behind the extra named `name`, as {@link collectExtras}
 * names it, or `undefined` when there is none or it cannot be read.
 */
export const extraValue = (
  el: Element,
  name: string
): {value: unknown} | undefined => {
  const entry = collectEntries(el).find((e) => e.extra.name === name);
  if (entry?.raw === undefined) return undefined;
  try {
    return {value: entry.raw()};
  } catch {
    return undefined;
  }
};

/** Own data fields (no accessors), in definition order. */
const ownFields = (host: Dict): Array<[string, unknown]> => {
  const fields: Array<[string, unknown]> = [];
  for (const key of Object.keys(host)) {
    const desc = Object.getOwnPropertyDescriptor(host, key);
    if (desc !== undefined && 'value' in desc) fields.push([key, desc.value]);
  }
  return fields;
};

/** A controller or signal stored in a field takes the field's name. */
const fieldNames = (fields: Array<[string, unknown]>): Map<object, string> => {
  const names = new Map<object, string>();
  for (const [key, value] of fields) {
    if (isObject(value) && !names.has(value)) names.set(value, clip(key));
  }
  return names;
};

/** Whether a field is Lit's own, declared, a function or already listed. */
const isSkippedField = (
  key: string,
  value: unknown,
  declared: Map<PropertyKey, unknown> | undefined,
  listed: Set<object>
): boolean =>
  key.startsWith('__') ||
  key.startsWith('_$') ||
  LIT_FIELDS.has(key) ||
  declared?.has(key) === true ||
  typeof value === 'function' ||
  (isObject(value) && (listed.has(value) || value instanceof Node));

const fieldEntry = (key: string, value: unknown): ExtraEntry => ({
  extra: {
    kind: 'field',
    name: clip(key),
    value: serialize(value),
    type: typeTag(value),
    ...(isExpandable(value) ? {expandable: true} : {}),
  },
  raw: () => value,
});

const isFull = (out: ExtraEntry[]): boolean => out.length >= MAX_EXTRAS;

/** Controllers first, in registration order; each is marked listed. */
const addControllers = (
  el: Element,
  names: Map<object, string>,
  out: ExtraEntry[],
  listed: Set<object>
): void => {
  for (const c of controllersOf(el)) {
    if (isFull(out)) break;
    listed.add(c);
    const entry = classify(c as Dict, names.get(c) ?? ctorName(c), true);
    if (entry !== undefined) out.push(entry);
  }
};

/** Signals and tasks held in own fields. */
const addHeldExtras = (
  fields: Array<[string, unknown]>,
  out: ExtraEntry[],
  listed: Set<object>
): void => {
  for (const [key, value] of fields) {
    if (isFull(out)) break;
    if (!isObject(value) || listed.has(value) || value instanceof Node) {
      continue;
    }
    const entry = classify(value, clip(key), false);
    if (entry === undefined) continue;
    listed.add(value);
    out.push(entry);
  }
};

/** Whatever own fields are left, as plain rows. */
const addPlainFields = (
  fields: Array<[string, unknown]>,
  declared: Map<PropertyKey, unknown> | undefined,
  out: ExtraEntry[],
  listed: Set<object>
): void => {
  for (const [key, value] of fields) {
    if (isFull(out)) break;
    if (isSkippedField(key, value, declared, listed)) continue;
    out.push(fieldEntry(key, value));
  }
};

const collectEntries = (el: Element): ExtraEntry[] => {
  try {
    const declared = (
      el.constructor as {elementProperties?: Map<PropertyKey, unknown>}
    ).elementProperties;
    const fields = ownFields(el as unknown as Dict);
    const out: ExtraEntry[] = [];
    const listed = new Set<object>();
    addControllers(el, fieldNames(fields), out, listed);
    addHeldExtras(fields, out, listed);
    addPlainFields(fields, declared, out, listed);
    return out;
  } catch {
    return [];
  }
};
